import { Map } from "@iventis/mapbox-gl";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { Feature, Polygon, Point } from "geojson";
import { BehaviorSubject } from "rxjs";
import { AreaModelStyle } from "@iventis/domain-model/model/areaModelStyle";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import centroid from "@turf/centroid";
import { v4 as uuid } from "uuid";
import { ModelDataStore } from "../../data-store/model-data-store";
import { MapModuleLayer, MapState, LayerStorageScope } from "../../types/store-schema";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { MapboxModelBaseLayer } from "./mapbox-model-base-layer";
import { MapboxAreaBaseLayer } from "./mapbox-area-base-layer";
import { LayerSourceType } from "../../types/internal";
import { removeLayerFilterByTypeAndId, updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { removeAdditionalLayerSuffixes } from "../../utilities/layer.helpers";
import { Layer } from "../generic-layers/layer";

/** Maps area model style properties to their area style equivalent */
const areaModelStyleToAreaStyleMapping = {
    areaColour: "colour",
    areaOpacity: "opacity",
};

function getAreaPointLayerId(layerId: string) {
    return `${layerId}_area_points`;
}

export class MapboxAreaModelLayer extends Layer<Feature<Polygon, MapObjectProperties>[], AreaModelStyle> {
    public readonly layerId: string;

    public readonly sourceId: string;

    private readonly source: string;

    private removed = false;

    private areaLayer: MapboxAreaBaseLayer;

    private modelLayer: MapboxModelBaseLayer<Feature<AnySupportedGeometry, MapObjectProperties>[], AreaModelStyle>;

    private readonly storageScope: LayerStorageScope;

    private hasOutline = false;

    /** Holds the deleted map object ids, for the model layer filter */
    private deletedMapObjectIds: string[] = [];

    public get layerAddedToMap() {
        return this.modelLayer.layerAddedToMap;
    }

    constructor(
        private readonly map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        private config: {
            getAboveLayerId: (layerId: string) => string;
            refreshTileSource: (sourceNames: string[]) => Promise<void>;
        }
    ) {
        super(state$);
        this.storageScope = layer.storageScope;
        this.source = layer.source;
        this.layerId = layer.id;
        this.sourceId = layer.id;

        const modelLayerId = getAreaPointLayerId(layer.id);
        const modelLayer = { ...layer, id: modelLayerId, source: layer.storageScope === LayerStorageScope.LocalOnly ? modelLayerId : layer.source };
        this.modelLayer = new MapboxModelBaseLayer<Feature<AnySupportedGeometry, MapObjectProperties>[], AreaModelStyle>(
            map,
            modelLayer,
            state$,
            modelDataStore,
            { name: layer.name, type: "model" },
            {
                getAboveLayerId: () => config.getAboveLayerId(removeAdditionalLayerSuffixes(layer.id)),
            }
        );

        this.areaLayer = new MapboxAreaBaseLayer(map, layer, state$, {
            getAboveLayerId: () => modelLayerId,
            getStyle: (layer) => ({ colour: layer.areaModelStyle.areaColour, opacity: layer.areaModelStyle.areaOpacity }),
        });
    }

    public async initaliseLayer(layer: MapModuleLayer) {
        await this.modelLayer.initaliseLayer(layer);

        // Check if the layer has been removed since we initalised the model layer
        if (this.removed) {
            return;
        }

        this.areaLayer.addLayer(this.storageScope === LayerStorageScope.LocalOnly ? "local" : "remote");
        // If it's local only we need to set the initial source
        if (this.storageScope === LayerStorageScope.LocalOnly) {
            const features = this.getGeoJson();
            if (features != null) {
                this.updateLocalSource(features);
            }
        }
    }

    public addLayer(layerSourceType: LayerSourceType): void {
        if (layerSourceType === "remote" || (layerSourceType === "local" && this.modelLayer.hasLocalLayer)) {
            this.modelLayer.addLayer(layerSourceType);
        }
        this.areaLayer.addLayer(layerSourceType);
    }

    public removeLayer(): void {
        this.modelLayer.removeLayer();
        this.areaLayer.removeLayer();
        this.removed = true;
    }

    public addLocalSource(): void {
        this.modelLayer.addLocalSource();
        this.areaLayer.addLocalSource();
    }

    public updateLocalSource(source: Feature<Polygon, MapObjectProperties>[], drawingSource?: Feature<Polygon, MapObjectProperties>[]): void {
        if (!this.layerAddedToMap) {
            return;
        }
        // If an outline is present, update the outline source
        if (this.hasOutline && drawingSource) {
            this.areaLayer.outlineLayer.updateLocalSource(
                drawingSource.map((feature) => ({ ...feature, geometry: { type: "LineString", coordinates: feature.geometry.coordinates[0] } }))
            );
        }
        // If the area model layer has LocalOnly storage scope, we only support centroid models for now (used in preview mode)
        if (this.modelLayer.hasLocalLayer) {
            const centroidModelFeatures = source.reduce<Feature<Point, MapObjectProperties>[]>((acc, feature) => {
                if (feature.geometry.coordinates[0].length < 4) {
                    return acc;
                }
                const centroidFeature = centroid(feature);
                return [...acc, { ...centroidFeature, properties: { ...feature.properties, id: uuid(), baseMapObjectId: feature.properties.id } }];
            }, []);
            this.modelLayer.updateLocalSource(centroidModelFeatures);
        }
        this.areaLayer.updateLocalSource(source, drawingSource);
    }

    public removeLocalSource(): void {
        this.modelLayer.removeLocalSource();
        this.areaLayer.removeLocalSource();
    }

    public hideLayer(): void {
        this.modelLayer.hideLayer();
        this.areaLayer.hideLayer();
    }

    public showLayer(): void {
        this.modelLayer.showLayer();
        this.areaLayer.showLayer();
    }

    public updateMapLevel(level: number): void {
        this.modelLayer.updateMapLevel(level);
        this.areaLayer.updateMapLevel(level);
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.modelLayer.updateMapOrder(aboveLayerId);
        this.areaLayer.updateMapOrder(this.modelLayer.layerId);
    }

    public addDateFilter() {
        const layer = this.getIventisLayer();
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.modelLayer.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
            this.areaLayer.addDateFilter();
        }
    }

    public removeDateFilter() {
        this.modelLayer.removeDateFilter();
        this.areaLayer.removeDateFilter();
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<AreaModelStyle>): Promise<void> {
        const propertiesAffectingAreaStyle: (keyof AreaModelStyle)[] = ["areaColour", "areaOpacity"];
        const areaStyleChanges: Partial<AreaStyle> = {};
        const modelStyleChanges: Partial<AreaModelStyle> = {};
        for (const [styleProperty, value] of Object.entries(styleChanges)) {
            if (propertiesAffectingAreaStyle.some((positionalChange) => positionalChange === styleProperty)) {
                areaStyleChanges[areaModelStyleToAreaStyleMapping[styleProperty]] = value;
            } else {
                modelStyleChanges[styleProperty] = value;
            }
        }
        await this.modelLayer.updateStyle(modelStyleChanges);
        await this.areaLayer.updateStyle(areaStyleChanges);
    }

    public getLayerIdsForSnapping(): string[] {
        return this.areaLayer.getLayerIdsForSnapping();
    }

    public onDrawingStarted(objectIds: string[]) {
        super.onDrawingStarted(objectIds);
        this.hasOutline = this.getIventisLayer()?.areaModelStyle?.areaOpacity?.staticValue?.staticValue === 0;
        // If the layer is not yet added to the map, there's no need to hide things or add the outline
        if (this.layerAddedToMap) {
            // Need to pass the deleted map object ids to ensure they stay filtered
            this.hideModelsOnObjects([...objectIds, ...this.deletedMapObjectIds]);
            this.areaLayer.onDrawingStarted(objectIds);
            if (this.hasOutline) {
                this.areaLayer.outlineLayer.addLocalSource();
            }
        }
    }

    public onDrawingFinished(objectIds: string[]) {
        super.onDrawingFinished(objectIds);
        if (this.storageScope === LayerStorageScope.LocalOnly) {
            this.unhideModelsOnObjects(objectIds);
        }
        this.areaLayer.outlineLayer.updateLocalSource([]);
        this.hasOutline = false;
    }

    public deleteMapObjects(deletedMapObjectIds: string[]): void {
        this.deletedMapObjectIds.push(...deletedMapObjectIds);
        this.hideModelsOnObjects(this.deletedMapObjectIds);
    }

    public restoreMapObjects(restoredMapObjectIds: string[]): void {
        this.unhideModelsOnObjects(restoredMapObjectIds);
        this.deletedMapObjectIds = this.deletedMapObjectIds.filter((mapObjectId) => !restoredMapObjectIds.includes(mapObjectId));
    }

    public onTileOnlyMapObjectsUpdated(objectIds: string[]) {
        if (this.isDrawing) {
            return;
        }
        if (this.storageScope !== LayerStorageScope.LocalOnly) {
            this.config
                .refreshTileSource([this.source])
                .then(() => {
                    if (!this.removed) {
                        // Once the tiles are refreshed all the filters can be removed as the tiles will have the updated data on which map objects have been deleted
                        this.unhideModelsOnObjects([...objectIds, ...this.deletedMapObjectIds]);
                        this.deletedMapObjectIds = [];
                    }
                })
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error("Failed to refresh tile source:", error);
                });
        }
    }

    public hideTileOnlyMapObjects(objectIds: string[]): void {
        if (this.isDrawing) {
            return;
        }
        if (this.storageScope !== LayerStorageScope.LocalOnly) {
            this.hideModelsOnObjects([...objectIds, ...this.deletedMapObjectIds]);
        }
    }

    public highlightObjects(objectIds: string[]): void {
        this.areaLayer.highlightObjects(objectIds);
    }

    public removeHighlights(): void {
        this.areaLayer.removeHighlights();
    }

    private hideModelsOnObjects(objectIds: string[]) {
        const currentFilter = this.map.getFilter(this.modelLayer.layerId);
        const modelFilters = objectIds.map((objectId) => ["!=", ["get", "baseMapObjectId"], objectId]);
        const updatedFilter = updateLayerFilters(currentFilter, ...modelFilters);
        this.map.setFilter(this.modelLayer.layerId, updatedFilter);
    }

    private unhideModelsOnObjects(objectIds: string[]) {
        const currentFilter = this.map.getFilter(this.modelLayer.layerId);
        // Remove the filter for all map objects
        const updatedFilter = removeLayerFilterByTypeAndId(currentFilter, FilterType.AreaModels, objectIds);
        this.map.setFilter(this.modelLayer.layerId, updatedFilter);
    }
}
